/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeAttachment,
  DimeAttachmentAction,
  DimeAttachmentActions,
  DimeAttachmentContent,
  DimeAttachmentDescription,
  DimeAttachmentGroup,
  DimeAttachmentMedia,
  DimeAttachmentTitle,
  DimeAttachmentTrigger,
  DimeDialog,
  DimeDialogContent,
  DimeDialogDescription,
  DimeDialogHeader,
  DimeDialogTitle,
  DimeDialogTrigger,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Check, Clock, FileText, RotateCw, X } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/attachment')({
  component: AttachmentDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="rounded-lg border border-border bg-background p-6">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </div>
  );
}

const STATES = [
  {
    value: 'idle',
    title: 'report-idle.csv',
    text: 'Ready to upload',
    tableDescription: 'Empty drop zone — dashed border, awaiting a file.',
  },
  {
    value: 'uploading',
    title: 'report-uploading.csv',
    text: 'Uploading · 64%',
    tableDescription: 'File is transferring — animated spinner, shimmering title.',
  },
  {
    value: 'processing',
    title: 'report-processing.csv',
    text: 'Processing document',
    tableDescription: 'Server is processing the upload — animated spinner, shimmering title.',
  },
  {
    value: 'error',
    title: 'report-error.csv',
    text: 'Upload failed — try again.',
    tableDescription: 'Upload failed — destructive border, icon, and description text.',
  },
  {
    value: 'done',
    title: 'report-done.csv',
    text: 'Uploaded · 1.8 MB',
    tableDescription: 'Upload complete — default resting state.',
  },
] as const;

const SIZES = ['default', 'sm', 'xs'] as const;

const PROPS = [
  {
    prop: 'state',
    values: 'idle | uploading | processing | error | done',
    description: 'Upload lifecycle status. Drives border, shimmer, and destructive styling.',
  },
  {
    prop: 'size',
    values: 'default | sm | xs',
    description: 'Overall card density — spacing, text size, and media size.',
  },
  {
    prop: 'orientation',
    values: 'horizontal | vertical',
    description: 'Layout direction. Vertical stacks the media above the content.',
  },
  {
    prop: 'variant (DimeAttachmentMedia)',
    values: 'icon | image',
    description: 'icon centers a lucide icon; image fills the slot with an <img>.',
  },
] as const;

function AttachmentDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Attachment</h1>
      <p className="mb-8 text-muted-foreground">
        A card for representing a file or image attachment, with media, metadata, upload state, and
        actions. Compose with <code className="font-mono text-sm">DimeAttachmentMedia</code>,{' '}
        <code className="font-mono text-sm">DimeAttachmentContent</code>, and{' '}
        <code className="font-mono text-sm">DimeAttachmentActions</code>.
      </p>

      <DocsSection title="Composition">
        <Preview>
          <DimeAttachment>
            <DimeAttachmentMedia variant="icon">
              <FileText />
            </DimeAttachmentMedia>
            <DimeAttachmentContent>
              <DimeAttachmentTitle>Invoice-2048.pdf</DimeAttachmentTitle>
              <DimeAttachmentDescription>248 KB</DimeAttachmentDescription>
            </DimeAttachmentContent>
            <DimeAttachmentActions>
              <DimeAttachmentAction aria-label="Remove Invoice-2048.pdf">
                <X />
              </DimeAttachmentAction>
            </DimeAttachmentActions>
          </DimeAttachment>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeAttachmentMedia variant="icon"</code> for a lucide
          icon, paired with <code className="font-mono">DimeAttachmentContent</code> for
          title/description and <code className="font-mono">DimeAttachmentActions</code> for
          trailing controls.
        </p>
        <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-xs">
          <code>{`import {
  DimeAttachment,
  DimeAttachmentMedia,
  DimeAttachmentContent,
  DimeAttachmentTitle,
  DimeAttachmentDescription,
  DimeAttachmentActions,
  DimeAttachmentAction,
} from '@nudool/design-system';

<DimeAttachment>
  <DimeAttachmentMedia variant="icon">
    <FileText />
  </DimeAttachmentMedia>
  <DimeAttachmentContent>
    <DimeAttachmentTitle>Invoice-2048.pdf</DimeAttachmentTitle>
    <DimeAttachmentDescription>248 KB</DimeAttachmentDescription>
  </DimeAttachmentContent>
  <DimeAttachmentActions>
    <DimeAttachmentAction aria-label="Remove Invoice-2048.pdf">
      <X />
    </DimeAttachmentAction>
  </DimeAttachmentActions>
</DimeAttachment>`}</code>
        </pre>
      </DocsSection>

      <DocsSection title="Image">
        <Preview>
          <DimeAttachmentGroup>
            {[
              { title: 'chart-q2.png', size: '1.1 MB' },
              { title: 'floorplan.png', size: '820 KB' },
            ].map(({ title, size }) => (
              <DimeDialog key={title}>
                <DimeAttachment orientation="vertical">
                  <DimeDialogTrigger asChild>
                    <DimeAttachmentTrigger aria-label={`Open ${title}`} />
                  </DimeDialogTrigger>
                  <DimeAttachmentMedia variant="image">
                    <img
                      src="https://placehold.co/96x96"
                      alt={title}
                      className="size-full object-cover"
                    />
                  </DimeAttachmentMedia>
                  <DimeAttachmentContent>
                    <DimeAttachmentTitle>{title}</DimeAttachmentTitle>
                    <DimeAttachmentDescription>{size}</DimeAttachmentDescription>
                  </DimeAttachmentContent>
                </DimeAttachment>
                <DimeDialogContent>
                  <DimeDialogHeader>
                    <DimeDialogTitle>{title}</DimeDialogTitle>
                    <DimeDialogDescription>{size}</DimeDialogDescription>
                  </DimeDialogHeader>
                  <img
                    src="https://placehold.co/400x400"
                    alt={title}
                    className="mx-auto w-full max-w-sm rounded-md object-cover"
                  />
                </DimeDialogContent>
              </DimeDialog>
            ))}
          </DimeAttachmentGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeAttachmentMedia variant="image"</code> with an{' '}
          <code className="font-mono">&lt;img&gt;</code> for image thumbnails. Wrap the card in a{' '}
          <code className="font-mono">DimeAttachmentTrigger asChild</code> inside a{' '}
          <code className="font-mono">DimeDialogTrigger asChild</code> to make the whole thumbnail
          clickable and open a larger preview.
        </p>
      </DocsSection>

      <DocsSection title="States">
        <Preview>
          <div className="flex flex-col gap-3">
            {STATES.map(({ value, title, text }) => (
              <DimeAttachment
                key={value}
                state={value}
                className="w-full"
                data-testid={`attachment-state-${value}`}
              >
                <DimeAttachmentMedia variant="icon">
                  {value === 'idle' && <Clock />}
                  {value === 'error' && <AlertCircle />}
                  {value === 'done' && <Check />}
                  {(value === 'uploading' || value === 'processing') && <FileText />}
                </DimeAttachmentMedia>
                <DimeAttachmentContent>
                  <DimeAttachmentTitle>{title}</DimeAttachmentTitle>
                  <DimeAttachmentDescription>{text}</DimeAttachmentDescription>
                </DimeAttachmentContent>
                {value === 'error' && (
                  <DimeAttachmentActions>
                    <DimeAttachmentAction aria-label={`Retry ${title}`}>
                      <RotateCw />
                    </DimeAttachmentAction>
                    <DimeAttachmentAction aria-label={`Remove ${title}`}>
                      <X />
                    </DimeAttachmentAction>
                  </DimeAttachmentActions>
                )}
                {value !== 'idle' && value !== 'error' && (
                  <DimeAttachmentActions>
                    <DimeAttachmentAction aria-label={`Remove ${title}`}>
                      <X />
                    </DimeAttachmentAction>
                  </DimeAttachmentActions>
                )}
              </DimeAttachment>
            ))}
          </div>
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">State</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {STATES.map(({ value, tableDescription }) => (
              <tr key={value} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{value}</td>
                <td className="py-2 text-muted-foreground">{tableDescription}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-sm text-muted-foreground">
          If <code className="font-mono">DimeAttachmentMedia</code> is left empty, it automatically
          renders a <code className="font-mono">DimeSpinner</code> whenever the parent{' '}
          <code className="font-mono">DimeAttachment</code>'s{' '}
          <code className="font-mono">state="uploading"</code> or{' '}
          <code className="font-mono">state="processing"</code> — so teams don't need to remember to
          add a loading spinner themselves. Passing your own icon (as the examples above do) always
          takes precedence over the spinner, in every state.
        </p>
        <div className="mt-4 rounded-lg border border-border bg-background p-6">
          <div className="mx-auto w-full max-w-md">
            <DimeAttachment
              state="uploading"
              className="w-full"
              data-testid="attachment-auto-spinner"
            >
              <DimeAttachmentMedia variant="icon" />
              <DimeAttachmentContent>
                <DimeAttachmentTitle>report-uploading.csv</DimeAttachmentTitle>
                <DimeAttachmentDescription>Uploading · 64%</DimeAttachmentDescription>
              </DimeAttachmentContent>
              <DimeAttachmentActions>
                <DimeAttachmentAction aria-label="Remove report-uploading.csv">
                  <X />
                </DimeAttachmentAction>
              </DimeAttachmentActions>
            </DimeAttachment>
          </div>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Here <code className="font-mono">DimeAttachmentMedia</code> has no children, so it falls
          back to the spinner automatically while uploading — the title still shimmers since that
          comes from the primitive's built-in state styling, independent of the spinner fallback.
        </p>
      </DocsSection>

      <DocsSection title="Sizes">
        <Preview>
          <div className="flex flex-col gap-3">
            {SIZES.map((value) => (
              <DimeAttachment key={value} size={value}>
                <DimeAttachmentMedia variant="icon">
                  <FileText />
                </DimeAttachmentMedia>
                <DimeAttachmentContent>
                  <DimeAttachmentTitle>
                    {value.charAt(0).toUpperCase() + value.slice(1)}
                  </DimeAttachmentTitle>
                  <DimeAttachmentDescription>size="{value}"</DimeAttachmentDescription>
                </DimeAttachmentContent>
              </DimeAttachment>
            ))}
          </div>
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Size</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border">
              <td className="py-2 pr-4 font-mono text-xs">default</td>
              <td className="py-2 text-muted-foreground">Standard density for most contexts.</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-4 font-mono text-xs">sm</td>
              <td className="py-2 text-muted-foreground">Compact — use in tight panels.</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-4 font-mono text-xs">xs</td>
              <td className="py-2 text-muted-foreground">Extra compact — dense lists.</td>
            </tr>
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Group">
        <Preview>
          <DimeAttachmentGroup
            role="group"
            aria-label="Attached files"
            tabIndex={0}
            className="focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            {['spec.pdf', 'diagram.png', 'notes.txt', 'summary.docx', 'archive.zip'].map((name) => (
              <DimeAttachment key={name} orientation="vertical">
                <DimeAttachmentMedia variant="icon">
                  <FileText />
                </DimeAttachmentMedia>
                <DimeAttachmentContent>
                  <DimeAttachmentTitle>{name}</DimeAttachmentTitle>
                  <DimeAttachmentDescription>—</DimeAttachmentDescription>
                </DimeAttachmentContent>
              </DimeAttachment>
            ))}
          </DimeAttachmentGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap attachments in <code className="font-mono">DimeAttachmentGroup</code> for a
          horizontally scrollable row. Because this row is presentational (its overflow, not its
          items, is the interactive surface), give it{' '}
          <code className="font-mono">role="group"</code> and a descriptive{' '}
          <code className="font-mono">aria-label</code> so assistive tech announces it as a unit,
          and a <code className="font-mono">tabIndex={0}</code> so keyboard users can focus the row
          and scroll it with arrow keys — each attachment's own interactive elements (
          <code className="font-mono">DimeAttachmentTrigger</code>,{' '}
          <code className="font-mono">DimeAttachmentAction</code>) remain independently reachable
          via Tab.
        </p>
      </DocsSection>

      <DocsSection title="Trigger">
        <Preview>
          <DimeDialog>
            <DimeAttachment>
              <DimeDialogTrigger asChild>
                <DimeAttachmentTrigger aria-label="Open Contract-Amendment.pdf" />
              </DimeDialogTrigger>
              <DimeAttachmentMedia variant="icon">
                <FileText />
              </DimeAttachmentMedia>
              <DimeAttachmentContent>
                <DimeAttachmentTitle>Contract-Amendment.pdf</DimeAttachmentTitle>
                <DimeAttachmentDescription>312 KB</DimeAttachmentDescription>
              </DimeAttachmentContent>
              <DimeAttachmentActions>
                <DimeAttachmentAction aria-label="Remove Contract-Amendment.pdf">
                  <X />
                </DimeAttachmentAction>
              </DimeAttachmentActions>
            </DimeAttachment>
            <DimeDialogContent>
              <DimeDialogHeader>
                <DimeDialogTitle>Contract-Amendment.pdf</DimeDialogTitle>
                <DimeDialogDescription>312 KB · Uploaded 2026-07-20</DimeDialogDescription>
              </DimeDialogHeader>
            </DimeDialogContent>
          </DimeDialog>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeAttachmentTrigger</code> stretches to fill the card so the
          whole attachment is clickable, while{' '}
          <code className="font-mono">DimeAttachmentActions</code> stay independently clickable
          above it. Pair with <code className="font-mono">asChild</code> to drive a{' '}
          <code className="font-mono">DimeDialogTrigger</code>.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Values</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {PROPS.map(({ prop, values, description }) => (
              <tr key={prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{prop}</td>
                <td className="py-2 pr-4 font-mono text-xs">{values}</td>
                <td className="py-2 text-muted-foreground">{description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
